import { useCallback, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import { useDeleteProductMessageMutation } from '@/graphql/generated/hooks'
import { DeleteProductMessageInput } from '@/graphql/generated/schemas'

/**
 * Return type for useDeleteProductMessage hook
 */
export interface UseDeleteProductMessageReturnIProps {
  deleteProductMessage: (
    input: DeleteProductMessageInput
  ) => Promise<{ success: boolean; error?: unknown }>
  isDeleting: boolean
}

/**
 * Hook that handles deletion of product messages with proper error handling.
 */
export const useDeleteProductMessage =
  (): UseDeleteProductMessageReturnIProps => {
    const [deleteMutation] = useDeleteProductMessageMutation()
    const { showSuccess, showError } = useToasts()
    const [isDeleting, setIsDeleting] = useState(false)

    const deleteProductMessage = useCallback(
      async (
        input: DeleteProductMessageInput
      ): Promise<{ success: boolean; error?: unknown }> => {
        setIsDeleting(true)

        try {
          const result = await deleteMutation({
            variables: { input },
            refetchQueries: ['ProductMessages'],
            awaitRefetchQueries: true,
          })

          if (!result.data?.deleteProductMessage?.id) {
            throw new Error('No data returned from mutation')
          }

          showSuccess('Announcement banner deleted successfully.')

          return { success: true }
        } catch (error) {
          Sentry.captureException(error)
          showError('Failed to delete announcement banner.')

          return { success: false, error }
        } finally {
          setIsDeleting(false)
        }
      },
      [deleteMutation, showSuccess, showError]
    )

    return {
      deleteProductMessage,
      isDeleting,
    }
  }
